import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import AddToCartButton from '../components/AddToCartButton';
import PicadaBuilder from '../components/PicadaBuilder';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ChevronLeft, MessageCircle, ChevronRight, X, ZoomIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(price);
};

// Lightbox component for fullscreen image view
const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        aria-label="Cerrar"
      >
        <X size={32} />
      </button>
      
      {images.length > 1 && (
        <>
          <button 
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/30 p-3 rounded-full"
            aria-label="Imagen anterior"
          >
            <ChevronLeft size={32} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/30 p-3 rounded-full"
            aria-label="Imagen siguiente"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}
      
      <img 
        src={images[currentIndex]} 
        alt="Imagen ampliada"
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Dots indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <span 
              key={idx} 
              className={`w-2 h-2 rounded-full ${idx === currentIndex ? 'bg-white' : 'bg-white/40'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductoDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Build array of all images
  const getAllImages = () => {
    if (!product) return [];
    const imgs = [product.image_url];
    if (product.images && product.images.length > 0) {
      imgs.push(...product.images);
    }
    return imgs;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, settingsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products/${slug}`),
          axios.get(`${BACKEND_URL}/api/settings`),
        ]);
        setProduct(productRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Producto no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EFE1C4]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#EFE1C4]" data-testid="product-not-found">
        <h1 className="font-heading text-xl text-[#1E1E1E] mb-4">
          PRODUCTO NO ENCONTRADO
        </h1>
        <Link to="/productos">
          <Button className="btn-outline-warm rounded-[10px] h-11 px-6">
            Volver a productos
          </Button>
        </Link>
      </div>
    );
  }

  const inStock = product.stock > 0;

  return (
    <div className="bg-[#EFE1C4]" data-testid="producto-detail-page">
      {/* Breadcrumb */}
      <section className="py-4 bg-[#F5EBD4]">
        <div className="container-brand">
          <nav className="breadcrumb flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#7A5A2E]">Inicio</Link>
            <span>/</span>
            <Link to="/productos" className="hover:text-[#7A5A2E]">Productos</Link>
            <span>/</span>
            <span className="opacity-70">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox 
          images={getAllImages()}
          currentIndex={selectedImage}
          onClose={() => setLightboxOpen(false)}
          onNext={() => setSelectedImage(prev => (prev + 1) % getAllImages().length)}
          onPrev={() => setSelectedImage(prev => prev === 0 ? getAllImages().length - 1 : prev - 1)}
        />
      )}

      {/* Product Detail */}
      <section className="section-padding">
        <div className="container-brand">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Image Gallery */}
            <div className="lg:col-span-7">
              {/* Main Image */}
              <div className="relative group cursor-pointer" onClick={() => setLightboxOpen(true)}>
                <img
                  src={selectedImage === 0 ? product.image_url : (product.images?.[selectedImage - 1] || product.image_url)}
                  alt={`Producto: ${product.name}`}
                  className="w-full rounded-lg shadow-sm transition-transform group-hover:scale-[1.02]"
                  width="600"
                  height="400"
                  decoding="async"
                  data-testid="product-detail-hero-image"
                />
                {/* Zoom indicator */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-lg flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 bg-black/60 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-opacity">
                    <ZoomIn size={18} /> Ampliar
                  </span>
                </div>
                {/* Navigation arrows if multiple images */}
                {product.images && product.images.length > 0 && (
                  <>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev === 0 ? product.images.length : prev - 1); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      aria-label="Imagen anterior"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev === product.images.length ? 0 : prev + 1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      aria-label="Imagen siguiente"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>
              {/* Thumbnails */}
              {product.images && product.images.length > 0 && (
                <div className="flex gap-2 mt-4 overflow-x-auto">
                  <button 
                    onClick={() => setSelectedImage(0)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === 0 ? 'border-[#7A5A2E]' : 'border-transparent hover:border-[#8C6A3F]/50'}`}
                  >
                    <img src={product.image_url} alt="Miniatura principal" className="w-full h-full object-cover" />
                  </button>
                  {product.images.map((img, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setSelectedImage(idx + 1)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImage === idx + 1 ? 'border-[#7A5A2E]' : 'border-transparent hover:border-[#8C6A3F]/50'}`}
                    >
                      <img src={img} alt={`Miniatura ${idx + 2}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-5">
              <Link 
                to="/productos" 
                className="inline-flex items-center gap-1 font-body text-sm text-[#4E3A21] hover:text-[#7A5A2E] mb-4"
              >
                <ChevronLeft size={16} />
                Volver a productos
              </Link>

              <h1 className="font-heading text-2xl md:text-3xl text-[#1E1E1E] mb-2">
                {product.name}
              </h1>

              {/* Price */}
              <p className="font-heading text-2xl text-[#7A5A2E] mb-4" data-testid="product-price">
                {formatPrice(product.price)}
              </p>

              <p className="font-body text-base text-[#1E1E1E] opacity-90 mb-6">
                {product.description}
              </p>

              {/* Add to Cart o Picada Builder */}
              {slug === 'picada-de-campo' ? (
                <div className="mb-6">
                  <PicadaBuilder picadaProduct={product} />
                </div>
              ) : (
                <div className="mb-6">
                  {inStock ? (
                    <AddToCartButton product={product} className="w-full sm:w-auto" />
                  ) : (
                    <p className="font-body text-sm text-red-600">Sin stock disponible</p>
                  )}
                  {inStock && (
                    <p className="font-body text-xs text-[#4E3A21] opacity-60 mt-2">
                      Stock disponible: {product.stock} unidades
                    </p>
                  )}
                </div>
              )}

              <Separator className="separator-gold my-6" />

              {/* Ingredients */}
              <section aria-labelledby="ingredientes" data-testid="product-detail-ingredients-section">
                <h2 id="ingredientes" className="font-heading text-sm md:text-base text-[#1E1E1E] mb-2">
                  INGREDIENTES
                </h2>
                <p className="font-body text-sm text-[#4E3A21] opacity-90">
                  {product.ingredients}
                </p>
              </section>

              <Separator className="separator-gold my-6" />

              {/* Elaboration */}
              <section aria-labelledby="elaboracion" data-testid="product-detail-elaboration-section">
                <h2 id="elaboracion" className="font-heading text-sm md:text-base text-[#1E1E1E] mb-2">
                  ELABORACIÓN
                </h2>
                <p className="font-body text-sm text-[#4E3A21] opacity-90">
                  {product.elaboration}
                </p>
              </section>

              <Separator className="separator-gold my-6" />

              {/* Conservation */}
              <section aria-labelledby="conservacion" data-testid="product-detail-conservation-section">
                <h2 id="conservacion" className="font-heading text-sm md:text-base text-[#1E1E1E] mb-2">
                  CONSERVACIÓN
                </h2>
                <p className="font-body text-sm text-[#4E3A21] opacity-90">
                  {product.conservation}
                </p>
              </section>

              <Separator className="separator-gold my-6" />

              {/* WhatsApp CTA */}
              <div className="mt-8">
                <p className="font-body text-sm text-[#4E3A21] opacity-70 mb-4">
                  ¿Tenés alguna consulta sobre este producto?
                </p>
                <a
                  href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, '')}?text=Hola, tengo una consulta sobre: ${product.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-detail-whatsapp-cta"
                >
                  <Button className="btn-outline-warm rounded-[10px] h-11 px-6 flex items-center gap-2">
                    <MessageCircle size={18} />
                    Consultanos por WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductoDetailPage;
